import { useCallback, useMemo } from 'react';

import { useAnalyticsEvents } from '../../common/analytics/generated/use-analytics-events';
import { logException, setupSentryClient } from '../../services/sentry';
import { SENTRY_PRODUCT, SENTRY_TEAM_NAME } from '../../services/sentry/constants';
import type { NavErrorBoundaryConfig } from '../../ui/types';

export function useNavBoundaryContext({
	location = 'unknown',
	disableSentry = false,
	disableAnalytics = false,
	onError,
	sentryConfig,
	tags,
}: NavErrorBoundaryConfig = {}): NavBoundaryContextType {
	const { fireEvent } = useAnalyticsEvents();

	if (!disableSentry) {
		try {
			setupSentryClient({ sentryConfig });
		} catch (error) {
			// Log failure to initialize Sentry
			fireEvent('operational.initSentry.failed', {
				location,
				reason: error instanceof Error ? error.message : 'unknown',
			});
		}
	}

	const tagsToLog = useMemo(() => {
		return {
			...tags,
			location,
			teamName: SENTRY_TEAM_NAME,
			product: sentryConfig?.product ?? SENTRY_PRODUCT,
			productBuild: getProductBuild(),
		};
	}, [tags, location, sentryConfig?.product]);

	const handleError = useCallback(
		(error: Error) => {
			// Send events to sentry
			if (!disableSentry) {
				try {
					logException(error, tagsToLog);
				} catch (err) {
					// Log failure to send to Sentry
					fireEvent('operational.logToSentry.failed', {
						location,
						reason: err instanceof Error ? err.message : 'unknown',
					});
				}
			}

			// Fire Analytics event
			if (!disableAnalytics) {
				fireEvent('operational.errorBoundary.unhandledErrorCaught', {
					location,
					errorMessage: error.message,
					errorStack: error.stack || 'no stack trace',
				});
			}

			// Invoke external onError
			onError?.(error);
		},
		[disableSentry, disableAnalytics, onError, location, tagsToLog, fireEvent],
	);

	return { handleError };
}

export type NavBoundaryContextType = {
	handleError: (error: Error, errorInfo?: React.ErrorInfo) => void;
};

function getProductBuild() {
	// Ensure we don't invoke window object in SSR
	if (typeof window === 'undefined') {
		return 'unknown';
	}

	switch (true) {
		// Jira - window.BUILD_KEY
		case 'BUILD_KEY' in window:
			return (window as typeof window & { BUILD_KEY?: string }).BUILD_KEY || 'unknown';
		// Confluence - window.__buildInfo
		case '__buildInfo' in window:
			return (
				(window as typeof window & { __buildInfo?: { FRONTEND_VERSION?: string } }).__buildInfo
					?.FRONTEND_VERSION || 'unknown'
			);
		default:
			return 'unknown';
	}
}
